/**
 * The party from the home hero, as 8-bit shadows walking in place across the
 * header: the crewmate at the back, then the ranger, mage, scout, marine,
 * warrior and companion, with the banner bearer out in front. Pure decoration
 * (aria-hidden, no pointer events).
 *
 * Each walker is a tiny sprite drawn as text below ('#' is a pixel of shadow):
 * its upper body, one of a few leg sets, and optional per-frame touches (a
 * swinging arm, the pennant's flutter). The four frames sit side by side in one
 * SVG and a CSS steps() animation slides them past its window, a whole pixel at
 * a time, so they stay crisp at any zoom. Everything is currentColor, so the
 * theme decides the shade (see .header-party in styles.css).
 *
 * The party sits in whatever room is left between the last menu entry and the
 * account controls, re-measured as either changes (the menu differs per viewer
 * and per admin), and simply isn't drawn when there isn't room. Behind them, the
 * hero's clouds drift slowly the other way across that whole gap.
 */

import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import cloudA from '../assets/clouds/cloud-a.webp';
import cloudB from '../assets/clouds/cloud-b.webp';

type Rows = string[];
/** [column, row relative to the top, pixels ('#' draws, '.' clears)] */
type Touch = [number, number, string];

interface Walker {
  name: string;
  legs: keyof typeof LEGS;
  /** Column of the leg art's left edge. */
  hip: number;
  /** Head to hips; rises a pixel on the passing frames. */
  top: Rows;
  /** Per-frame touches, drawn relative to the top so they rise with it. */
  touch?: Touch[][];
  /** Seconds per stride (four frames). Short legs step quicker. */
  stride?: number;
}

// Stride frames (0, 2) and passing frames (1, 3), which are a row longer
// because the body rides a pixel higher over the planted foot.
const LEGS = {
  walk: {
    stride: ['..####..', '.##..##.', '.#....#.', '##....##', '#......#', '##.....##'],
    pass: ['..####..', '..####..', '...##...', '...##...', '...##...', '...##...', '...###..'],
  },
  robe: {
    stride: ['.######..', '.#######.', '.#######.', '#########', '#########', '##.....##'],
    pass: ['.######..', '.######..', '.#######.', '.#######.', '#########', '#########', '..####...'],
  },
  armour: {
    stride: ['..######..', '.###..###.', '.##....##.', '###....###', '##......##', '##......##', '###.....###'],
    pass: ['..######..', '..######..', '...####...', '...####...', '...####...', '...####...', '...####...', '...#####..'],
  },
  stubby: {
    stride: ['.##..##.', '##....##', '#......#'],
    pass: ['..####..', '..####..', '..####..', '..#####.'],
  },
  paws: {
    stride: ['.##.##.', '##...##'],
    pass: ['..###..', '..###..', '..####.'],
  },
} satisfies Record<string, { stride: Rows; pass: Rows }>;

const PARTY: Walker[] = [
  {
    name: 'crewmate',
    legs: 'stubby',
    hip: 3,
    stride: 0.8,
    top: [
      '.....####....',
      '....######...',
      '...########..',
      '...####....#.',
      '.######....#.',
      '.###########.',
      '.##########..',
      '.##########..',
      '.##########..',
      '...########..',
    ],
  },
  {
    // pointed hood hanging back, a longbow held upright at his side
    name: 'ranger',
    legs: 'walk',
    hip: 2,
    top: [
      '...###......',
      '..#####..#..',
      '.######...#.',
      '#.#####...#.',
      '..#####...##',
      '.#######..##',
      '##########.#',
      '#########..#',
      '#########..#',
      '.########.#.',
      '.#######..#.',
      '..######.#..',
      '..######....',
    ],
  },
  {
    // deep hood, robe to the ground, a staff with a crystal
    name: 'mage',
    legs: 'robe',
    hip: 1,
    top: [
      '.....##.....#.',
      '....####...###',
      '...######..###',
      '...#######..#.',
      '...######...#.',
      '..#######..##.',
      '.##########...',
      '.#########.#..',
      '.########..#..',
      '.########..#..',
      '..#######..#..',
      '..#######..#..',
      '..#######..#..',
    ],
  },
  {
    // hood up, a bedroll pack, a free arm swinging
    name: 'scout',
    legs: 'walk',
    hip: 3,
    top: [
      '.....###...',
      '....#####..',
      '....######.',
      '###.######.',
      '#########..',
      '##########.',
      '##########.',
      '#########..',
      '#########..',
      '.#########.',
      '..#######..',
      '...######..',
      '...######..',
    ],
    touch: [[[9, 7, '#'], [10, 8, '#'], [10, 9, '#']], [], [[2, 9, '#'], [2, 10, '#']], []],
  },
  {
    // helmet between huge pauldrons, the pack's exhausts, the bolter across his chest
    name: 'marine',
    legs: 'armour',
    hip: 3,
    stride: 1.4,
    top: [
      '.......####.....',
      '......######....',
      '..##..######....',
      '.####.#######...',
      '###############.',
      '################',
      '################',
      '.##############.',
      '.############..#',
      '..#########..##.',
      '..########..##..',
      '..#######..##...',
      '..########.#....',
      '...#######......',
      '...########.....',
      '...########.....',
    ],
  },
  {
    // helmet with a nose guard, cape streaming behind
    name: 'warrior',
    legs: 'walk',
    hip: 3,
    top: [
      '.....####...',
      '....######..',
      '....#######.',
      '....######..',
      '.....####...',
      '..#########.',
      '.##########.',
      '###########.',
      '##########..',
      '###.######..',
      '##..######..',
      '#...######..',
      '#...######..',
    ],
    touch: [[[10, 7, '##'], [11, 8, '#'], [11, 9, '#']], [], [], []],
  },
  {
    // round ears, a little snout, a cloak
    name: 'companion',
    legs: 'paws',
    hip: 2,
    stride: 0.7,
    top: [
      '.##...##..',
      '####.####.',
      '.########.',
      '.#########',
      '.########.',
      '..######..',
      '.########.',
      '.#######..',
      '..######..',
    ],
  },
  {
    // the League's pennant, raised; it flutters on the passing frames
    name: 'banner',
    legs: 'walk',
    hip: 2,
    top: [
      '.........#.....',
      '.........######',
      '.........#####.',
      '.........####..',
      '.........###...',
      '.........#.....',
      '.........#.....',
      '....###..#.....',
      '...#####.#.....',
      '...######......',
      '...#####.#.....',
      '....###..#.....',
      '..######.#.....',
      '.#########.....',
      '.########......',
      '.#######.#.....',
      '.#######.#.....',
      '..######.#.....',
      '..######.#.....',
      '..######.#.....',
    ],
    touch: [[], [[14, 1, '.'], [14, 2, '#'], [13, 3, '#']], [], [[14, 1, '.'], [14, 2, '#'], [13, 3, '#']]],
  },
];

const SCALE = 2; // screen pixels per sprite pixel
const GAP = 4; // sprite pixels between walkers
const STRIDE = 1.2; // default seconds per stride: an unhurried stroll

interface Sprite {
  name: string;
  w: number;
  h: number;
  /** One path per frame, the frames side by side. */
  paths: string[];
  stride: number;
}

/** A walker's four frames as rows of '#'/'.', feet on the last row. */
function frames(c: Walker): Rows[] {
  const legs = LEGS[c.legs];
  const w = Math.max(
    ...c.top.map((r) => r.length),
    c.hip + Math.max(...[...legs.stride, ...legs.pass].map((r) => r.length)),
    ...(c.touch ?? []).flat().map(([x, , s]) => x + s.length),
  );
  const h = c.top.length + legs.pass.length;
  return [0, 1, 2, 3].map((f) => {
    const legArt = f % 2 ? legs.pass : legs.stride;
    const grid = Array.from({ length: h }, () => Array<string>(w).fill('.'));
    const topY = h - legArt.length - c.top.length;
    const stamp = (x0: number, y0: number, rows: Rows, clear = false) =>
      rows.forEach((row, dy) =>
        [...row].forEach((ch, dx) => {
          const line = grid[y0 + dy];
          if (!line || x0 + dx >= w) return;
          if (ch === '#') line[x0 + dx] = '#';
          else if (clear) line[x0 + dx] = '.';
        }),
      );
    stamp(0, topY, c.top);
    stamp(c.hip, h - legArt.length, legArt);
    for (const [x, y, s] of c.touch?.[f] ?? []) stamp(x, topY + y, [s], true);
    return grid.map((r) => r.join(''));
  });
}

/** Rows of '#' as one path of pixel runs, shifted right by `dx`. */
function toPath(rows: Rows, dx: number): string {
  let d = '';
  rows.forEach((row, y) => {
    for (const m of row.matchAll(/#+/g)) d += `M${dx + m.index!} ${y}h${m[0].length}v1h-${m[0].length}z`;
  });
  return d;
}

const SPRITES: Sprite[] = PARTY.map((c) => {
  const fs = frames(c);
  const w = fs[0]![0]!.length;
  return { name: c.name, w, h: fs[0]!.length, paths: fs.map((f, k) => toPath(f, k * w)), stride: c.stride ?? STRIDE };
});
const PARTY_WIDTH = (SPRITES.reduce((sum, s) => sum + s.w, 0) + GAP * (SPRITES.length - 1)) * SCALE;
const MARGIN = 24; // clear space kept either side, px

// The hero's two cloud sprites (downsized copies), drifting right to left
// behind the party as the hero's sky does. `pace` is seconds per pixel
// travelled, so they keep the same slow speed however wide the gap is; `at` is
// how far across each starts; `top` is px from the top of the bar.
const CLOUDS = [
  { src: cloudA, w: 104, top: 0, pace: 0.045, at: 0.08 },
  { src: cloudB, w: 80, top: 14, pace: 0.055, at: 0.36, flip: true },
  { src: cloudA, w: 66, top: -4, pace: 0.065, at: 0.62, flip: true },
  { src: cloudB, w: 92, top: 8, pace: 0.05, at: 0.86 },
];

export default function HeaderFellowship({
  navRef,
  accountRef,
}: {
  navRef: RefObject<HTMLElement | null>;
  accountRef: RefObject<HTMLElement | null>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [gap, setGap] = useState<{ left: number; width: number } | null>(null);

  // A passive effect, not a layout one: the menu and account controls come
  // after this in the header, and their refs aren't attached until the layout
  // pass has reached them.
  useEffect(() => {
    const bar = ref.current?.parentElement;
    const nav = navRef.current;
    const account = accountRef.current;
    if (!bar || !nav || !account) return;

    const place = () => {
      const b = bar.getBoundingClientRect();
      const n = nav.getBoundingClientRect();
      const a = account.getBoundingClientRect();
      const last = nav.lastElementChild?.getBoundingClientRect();
      // When the bar wraps, the account controls drop to a row of their own
      // and there's no gap between them and the menu to walk in.
      const sameRow = a.top < n.bottom && n.top < a.bottom;
      const start = (last && last.width ? last.right : n.left) - b.left + MARGIN;
      const end = Math.min(a.left, b.right) - b.left - MARGIN;
      const room = Math.round(end - start);
      setGap(sameRow && room >= PARTY_WIDTH ? { left: Math.round(start), width: room } : null);
    };

    place();
    const sizes = new ResizeObserver(place);
    [bar, nav, account].forEach((el) => sizes.observe(el));
    // The menu changes per viewer and when an admin rearranges it.
    const entries = new MutationObserver(place);
    entries.observe(nav, { childList: true, subtree: true, characterData: true });
    void document.fonts?.ready.then(place);
    return () => {
      sizes.disconnect();
      entries.disconnect();
    };
  }, [navRef, accountRef]);

  return (
    <div
      ref={ref}
      className="header-party"
      aria-hidden="true"
      style={gap == null ? { visibility: 'hidden' } : { left: gap.left, width: gap.width }}
    >
      <div className="header-sky">
        {CLOUDS.map((c, i) => (
          <img
            key={i}
            className="header-cloud"
            src={c.src}
            alt=""
            width={c.w}
            style={
              {
                top: c.top,
                // Across the gap and off the far edge, at the cloud's own pace.
                '--room': `${gap?.width ?? 0}px`,
                animationDuration: `${((gap?.width ?? 0) + c.w) * c.pace}s`,
                animationDelay: `${-((gap?.width ?? 0) + c.w) * c.pace * c.at}s`,
                '--flip': c.flip ? -1 : 1,
              } as CSSProperties
            }
          />
        ))}
      </div>
      <div className="header-walkers">
        {SPRITES.map((s, i) => (
          <svg
            key={s.name}
            className="header-walker"
            width={s.w * SCALE}
            height={s.h * SCALE}
            viewBox={`0 0 ${s.w} ${s.h}`}
            shapeRendering="crispEdges"
            focusable="false"
            style={
              {
                '--w': s.w,
                '--stride': `${s.stride}s`,
                // Out of step with each other, like the hero loop.
                '--phase': `${-((i * 0.37) % s.stride)}s`,
                marginLeft: i ? GAP * SCALE : 0,
              } as CSSProperties
            }
          >
            <g className="header-walker-frames" fill="currentColor">
              {s.paths.map((d, k) => (
                <path key={k} d={d} />
              ))}
            </g>
          </svg>
        ))}
      </div>
    </div>
  );
}
